import "../../../styles/UserAuthPage.css";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useRedirectIfAuthenticated } from "../../../hooks/useRedirectIfAuthenticated";
import isAuthFormValid from "../../../utils/isAuthFormValid";
import { signInUser, signUpUser } from "../../../services/authService";
import Header from "./Header";
import TabToggleBtn from "./TabToggleBtn";
import FormInputField from "./FormInputField";
import SubmitBtn from "./SubmitBtn";
import Socials from "./Socials";

function UserForm() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [mode, setMode] = useState("signin");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [signInForm, setSignInForm] = useState({
    email: "",
    password: "",
  });

  const [signUpForm, setSignUpForm] = useState({
    fullName: "",
    occupation: "",
    state: "",
    lga: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const isSignIn = useMemo(() => mode === "signin", [mode]);

  // Custom hook that checks if user is authenticated and redirects to "/map" if so
  useRedirectIfAuthenticated(user, "/map");

  // ──────────────────────────────────────────────────────────
  // FORM FIELD CHANGE HANDLERS
  // ──────────────────────────────────────────────────────────
  // It looks at the input field being typed into and grabs:
  //   name  → which field it is (e.g "email" or "password")
  //   value → what the user just typed
  const handleSignInChange = (e) => {
    const { name, value } = e.target;
    setSignInForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignUpChange = (e) => {
    const { name, value } = e.target;
    setSignUpForm((prev) => ({ ...prev, [name]: value }));
  };

  // ──────────────────────────────────────────────────────────
  // TOGGLE BETWEEN SIGN IN AND SIGN UP
  // ──────────────────────────────────────────────────────────
  const handleModeChange = (newMode) => {
    if (newMode !== mode) {
      setErrorMessage("");
      setIsTransitioning(true);
      setTimeout(() => {
        setMode(newMode);
        setIsTransitioning(false);
      }, 150);
    }
  };

  // ──────────────────────────────────────────────────────────
  // SIGN UP HANDLER
  // ──────────────────────────────────────────────────────────
  const handleSignUp = async () => {
    setErrorMessage("");
    setIsLoading(true);

    try {
      if (!isAuthFormValid(false, signInForm, signUpForm, setErrorMessage)) return;
      // Sign up and create user profile in one step using signUpUser service which calls supabase.auth.signUp under the hood
      const { data, error } = await signUpUser(signUpForm);

      if (error) {
        setErrorMessage(error?.message || "Sign up failed");
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        // Wait a moment for profile to be created by trigger
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Redirect to map
        navigate("/map", { replace: true });
      }
    } catch (error) {
      setErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────
  // SIGN IN HANDLER
  // ──────────────────────────────────────────────────────────
  const handleSignIn = async () => {
    setErrorMessage("");
    setIsLoading(true);

    try {
      if (!isAuthFormValid(isSignIn, signInForm, signUpForm, setErrorMessage)) return;
      // Sign in
      const { data, error } = await signInUser(signInForm);

      if (error) {
        setErrorMessage(error?.message || "Sign in failed");
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        // Redirect to map
        navigate("/map", { replace: true });
      }
    } catch (error) {
      setErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────
  // FORM SUBMISSION
  // ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSignIn) await handleSignIn();
    else await handleSignUp();
  };

  return (
    <div className="authPage__formWrap">
      <div className="authPage__formCard">
        {/* HEADER — fades when switching between sign-in/sign-up */}
        <Header isTransitioning={isTransitioning} isSignIn={isSignIn} />

        {/* TAB TOGGLE */}
        <TabToggleBtn
          isSignIn={isSignIn}
          isLoading={isLoading}
          handleModeChange={handleModeChange}
        />

        {/* ERROR MESSAGE — shown when sign-in/up fails */}
        {errorMessage && (
          <div className="authPage__error" role="alert">
            <strong>Error:</strong> {errorMessage}
          </div>
        )}

        {/* FORM — fades when switching tabs */}
        <form
          onSubmit={handleSubmit}
          className={`authPage__form ${isTransitioning ? "authPage__form--transitioning" : ""}`}
        >
          {/* INPUT FIELDS */}
          <FormInputField
            isSignIn={isSignIn}
            signInForm={signInForm}
            signUpForm={signUpForm}
            handleSignInChange={handleSignInChange}
            handleSignUpChange={handleSignUpChange}
            isLoading={isLoading}
          />

          {/* SUBMIT */}
          <SubmitBtn isSignIn={isSignIn} isLoading={isLoading} />

          {/* SOCIAL LINKS */}
          <Socials />
        </form>
      </div>
    </div>
  );
}

export default UserForm;
